// © Microsoft Corporation. All rights reserved.

import { classNames } from "@microsoft/fast-web-utilities";
import { Comment, Content, User } from "@msnews/social-data-service";
import { avatarFallback, chevron_down, getAgoText, openGuidelinePage, openProfilePage, enums, exclamation_round, flag, hasThemeBase, isComponentDisabled, placeholderImages, shield, trash, addOrUpdateTelemetryObject, renderHybrid, comment } from "@msnews/social-utils";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import React from "react";
import DeleteMenu from "../../common/delete-menu/DeleteMenu";
import { SocialMostReactions } from "@msnews/social-most-reactions";
import ReactionPanel from "../../common/reaction-panel/ReactionPanel";
import ReportMenu from "../../common/report-menu/ReportMenu";
import { SocialCommentClassNameContract } from "../../SocialComment.classnames";
import { SocialCommentLocalizedStrings } from "../../SocialComment.localizedStrings";
import { CommentConnector, ComponentConnector, FollowInfo, PopupConnector, UsersConnector } from "../../SocialComment.props";
import DisabledMessage from "../disabled-message/DisabledMessage";
import ReplyList from "../reply-list/ReplyList";
import UserCard from "../user-card/UserCard";
import { commentItemTelemetryActionDispatchers, commentItemTelemetryContracts, CommentItemTelemetryObjects } from "./CommentItem.telemetry";

const minFooterSingleLineWidth = 375;

export interface CommentItemProps {
    componentConnector: ComponentConnector;
    commentConnector: CommentConnector;
    item: Comment;
    contentDisabled: boolean,
    me: User,
    t: SocialCommentLocalizedStrings;
    managedClasses?: SocialCommentClassNameContract;
    commentItemType: string,
    usersConnector: UsersConnector;
    popupConnector: PopupConnector;
    users: { [id: string]: FollowInfo;}; // dictionary definening
    market: string;
    content: Content;
    mainTelemetryObject: TelemetryObjectBase;
    theme: string;
    responsiveClass: string;
    isBlury?: boolean;
    commentDisplayType?: string;
    specificCommentId?: string;
}

interface CommentItemState {
    reportMenuVisible: boolean;
    deleteMenuVisible: boolean;
    selectedReportType: string;
    showReplyBox: boolean;
    reactionMenuVisible: boolean;
    userCardVisible: boolean;
    userCardUserId: string;
    nowDeleted: boolean;
    messageTruncated: boolean;
    isHoverUserCard: boolean;
    shouldFooterRenderTwoLines: boolean;
    mentionUserCardVisible: boolean;
}

class CommentItem extends React.Component<CommentItemProps, CommentItemState> {

    ref: HTMLLIElement;
    messageRef: React.RefObject<HTMLDivElement>;
    telemetryObjects: CommentItemTelemetryObjects;

    constructor(props) {
        super(props);
        this.state = {
            reportMenuVisible: false,
            deleteMenuVisible: false,
            selectedReportType: null,
            showReplyBox: false,
            reactionMenuVisible: false,
            userCardVisible: false,
            userCardUserId: "",
            nowDeleted: false,
            messageTruncated: null,
            isHoverUserCard: false,
            shouldFooterRenderTwoLines: false,
            mentionUserCardVisible: false
        };

        this.hideReplyBox = this.hideReplyBox.bind(this);
        this.ref = null;
        this.messageRef = React.createRef();
        this.telemetryObjects = null;
    }

    componentDidMount() {
        if (this.props.item.mentionUser && !(this.props.users && this.props.users[this.props.item.mentionUser])) {
            this.props.usersConnector.fetchUser(this.props.item.mentionUser);
        }
        if (this.props.item.addedOffline && this.props.commentItemType == "Reply" && this.ref) {
            this.ref.scrollIntoView({
                behavior: "smooth",
                block: "end"
            });
        }

        this.checkMessageTruncated();
        this.checkContainerWidth();
    }

    componentDidUpdate() {
        this.checkMessageTruncated();
        this.checkContainerWidth();
    }

    checkMessageTruncated() {
    /** Check if message is truncated by webkit clamp */
        if (this.messageRef && this.messageRef.current && this.state.messageTruncated == null) {
            this.messageRef.current.scrollHeight > this.messageRef.current.clientHeight + 10
                ? this.setState({messageTruncated: true})
                : this.setState({messageTruncated: false});
        }
    }

    /* REPORT */
    showReportMenu(e) {
        const { theme, popupConnector, item } = this.props;
        const isMobile = this.props.responsiveClass?.includes(enums.responsiveLevels.L1);
        const isEmbeded = isComponentDisabled(theme, "reportmenu");
        if (isEmbeded || isMobile) {
            popupConnector.openPopup("reportmenu", { id: item.id });
        } else {
            this.setState({
                reportMenuVisible: true
            });
        }

        commentItemTelemetryActionDispatchers.genericClick(e.currentTarget, this.telemetryObjects && this.telemetryObjects.reportButton);
    }

    showDeleteMenu(e) {
        this.setState({
            deleteMenuVisible: true
        });

        commentItemTelemetryActionDispatchers.genericClick(e.currentTarget, this.telemetryObjects && this.telemetryObjects.deleteButton);
    }

    reportHandler(reportType, e) {
        this.setState({
            reportMenuVisible: false,
            selectedReportType: reportType
        });
    }

    /* REPLY-BOX */
    showReplyBox(target?) {
        this.setState({
            showReplyBox: true
        });
    }

    hideReplyBox() {
        this.setState({
            showReplyBox: false
        });
    }

    replyButtonClickHandler() {
        if (this.state.showReplyBox) {
            this.hideReplyBox();
        } else {
            this.showReplyBox();
        }
    }

    /* REACTION */
    reactionHandler(type) {
        const sendCommentReaction = this.props.commentConnector.sendCommentReaction;

        const reset = this.props.item && this.props.item.rating == type;
        sendCommentReaction(this.props.item, type, reset);
    }

    onReactionsClick(e) {
        commentItemTelemetryActionDispatchers.genericClick(e.currentTarget, this.telemetryObjects && this.telemetryObjects.reactionsCount);
        this.props.popupConnector.openPopup("reactionBreakdownComment", {id: this.props.item.id, parentTelemetryObject: this.telemetryObjects && this.telemetryObjects.reactionBreakdown});
    }

    showUserCard(e, userId, isVisible=true, isHoverUserCard=true) {
        const isSuperApp = hasThemeBase(this.props.theme, enums.theme.superappBase);
        if (isSuperApp) {
            return;
        }
        this.setState({userCardVisible: isVisible, userCardUserId: userId, isHoverUserCard: isHoverUserCard, mentionUserCardVisible: false});
    }

    showMentionUserCard(e, userId, isHoverUserCard=true) {
        this.setState({mentionUserCardVisible: true, userCardUserId: userId, isHoverUserCard: isHoverUserCard, userCardVisible: false});
    }

    deleteCommentHandler(e, commentId, parentId, userId) {
        this.setState({
            reportMenuVisible: false,
            nowDeleted: true
        });
        this.props.commentConnector.deleteComment(commentId, parentId, userId);
    }

    render() {
        const { id, parentId, replyCount, repliesHasPrevious, repliesHasMore, user, createDatetime, isHidden, isInReview, isDeleted, text, visibleStatus, rating, replies, mentionUser: mentionUserId, addedOffline } = this.props.item;
        if (!user) { return null; }

        const { isBlury, componentConnector, responsiveClass, contentDisabled, me, t, managedClasses, commentItemType, commentConnector, usersConnector, popupConnector, users, market, content, mainTelemetryObject, theme, specificCommentId } = this.props;
        const {reportMenuVisible, selectedReportType, nowDeleted, messageTruncated, deleteMenuVisible, shouldFooterRenderTwoLines} = this.state;
        const isReply = parentId && parentId.length > 0;
        const hideReplyBox = this.hideReplyBox;
        const showAvatar = !selectedReportType && !isHidden;
        const showReplyBox = this.state.showReplyBox;
        const userCardVisible = me && !me.isBanned && !isHidden && !isDeleted;
        const reactionAllowed = me && !me.isBanned && !isHidden;
        const isSpecificComment = id === specificCommentId;
        const mentionUser = users && users[mentionUserId] && users[mentionUserId].data;

        if (mainTelemetryObject) {
            let commentItemContract;
            if (isDeleted) {
                commentItemContract = commentItemTelemetryContracts.deletedCommentItem;
            } else if (addedOffline) {
                commentItemContract = {...commentItemTelemetryContracts.commentItem, name: "CommentPosted"};
            } else {
                commentItemContract = commentItemTelemetryContracts.commentItem;
            }
            this.telemetryObjects = {
                commentItem: addOrUpdateTelemetryObject(mainTelemetryObject, commentItemContract, { id }, this.telemetryObjects && this.telemetryObjects.commentItem),
                deleteButton: addOrUpdateTelemetryObject(mainTelemetryObject, commentItemTelemetryContracts.deleteButton, { id }, this.telemetryObjects && this.telemetryObjects.deleteButton),
                reportButton: addOrUpdateTelemetryObject(mainTelemetryObject, commentItemTelemetryContracts.reportButton, { id }, this.telemetryObjects && this.telemetryObjects.reportButton),
                reactionsCount: addOrUpdateTelemetryObject(mainTelemetryObject, commentItemTelemetryContracts.reactionsCount, { id }, this.telemetryObjects && this.telemetryObjects.reactionsCount),
                reactionBreakdown: addOrUpdateTelemetryObject(mainTelemetryObject, commentItemTelemetryContracts.reactionBreakdown, { id }, this.telemetryObjects && this.telemetryObjects.reactionBreakdown),
                userNameLink: addOrUpdateTelemetryObject(mainTelemetryObject, {...commentItemTelemetryContracts.profileLink}, { id: user.traceId, headline: user.name}, this.telemetryObjects && this.telemetryObjects.userNameLink),
                userAvatarLink: addOrUpdateTelemetryObject(mainTelemetryObject, {...commentItemTelemetryContracts.profileLink}, { id: user.traceId, headline: user.name}, this.telemetryObjects && this.telemetryObjects.userAvatarLink),
                mentionUserLink: mentionUser && addOrUpdateTelemetryObject(mainTelemetryObject, {...commentItemTelemetryContracts.profileLink}, { id: mentionUser.traceId, headline: mentionUser.name}, this.telemetryObjects && this.telemetryObjects.mentionUserLink),
                deletedMessage: addOrUpdateTelemetryObject(mainTelemetryObject, {...commentItemTelemetryContracts.guidelinesLink}, { id }, this.telemetryObjects && this.telemetryObjects.deletedMessage)
            };
        }

        const isSuperApp = hasThemeBase(this.props.theme, enums.theme.superappBase);

        /* SUB ELEMENTS */
        let commentBody; // message
        if (isDeleted || nowDeleted) {
            commentBody =
                <a
                    className="cwt-link cwt-comment-deleted-message"
                    title={t.DisabledMessagesGuidelines}
                    {...openGuidelinePage(componentConnector, theme, market, "_blank")}
                    {...renderHybrid(this.telemetryObjects && this.telemetryObjects.deletedMessage, false)}
                >
                    {t[enums.visibleStatusMap[visibleStatus] || enums.visibleStatusMap.Default]}
                    <i className="cwt-icon cwt-disabled-message">{exclamation_round}</i>
                </a>;
        } else {
            commentBody =
                <React.Fragment>
                    {mentionUser &&
                        <React.Fragment>
                            <a
                                className="cwt-name cwt-link"
                                onMouseEnter={(e) => this.showMentionUserCard(e, mentionUserId)}
                                {...openProfilePage(componentConnector, theme, mentionUserId, market, "_blank")}
                                {...renderHybrid(this.telemetryObjects && this.telemetryObjects.mentionUserLink, false)}
                            >{mentionUser.name}</a>
                        &nbsp;
                            {this.state.mentionUserCardVisible &&
                                <UserCard
                                    {...{usersConnector, users, managedClasses, me, t, id: this.state.userCardUserId, visible: true, market,
                                        onHide: () => this.setState({mentionUserCardVisible: false}),
                                        triggerRef: { current: this.ref } as React.RefObject<any>,
                                        isHover: this.state.isHoverUserCard,
                                        mainTelemetryObject,
                                        responsiveClass
                                    }}
                                />
                            }
                        </React.Fragment>
                    }
                    <div dangerouslySetInnerHTML={{__html: text}} />
                </React.Fragment>;
        }

        const isSelf = me && user && me.id == user.id;
        const avatarUrl = showAvatar && user.avatarStatus == enums.userAvatarStatusType.normal && user.avatarUrl || placeholderImages.avatar;

        let innerContent; // disabled message OR header, body, footer
        if (isInReview) { // IN REVIEW
            innerContent = <DisabledMessage {...{ managedClasses, message: t[enums.visibleStatusMap[visibleStatus]], t, hideIcon: true }} />;
        } else if (selectedReportType) { // REPORTED
            const reportedCommentItemTelemetryObject = addOrUpdateTelemetryObject(mainTelemetryObject, commentItemTelemetryContracts.reportedCommentItem, { id });
            innerContent = <DisabledMessage {...{ managedClasses, message: t.VisibleStatusInReview, t, hideIcon: true, mainTelemetryObject: reportedCommentItemTelemetryObject }} />;
        } else if (nowDeleted) { // REPORTED
            innerContent = <DisabledMessage {...{ managedClasses, message: t.CommentDeletedMsg, t, hideIcon: true }} />;
        } else {
            innerContent = <React.Fragment>
                <section>
                    <header className="cwt-header">
                        {isHidden
                            ? <span className="cwt-deleted-name">{t.CommentItemDeletedUserName}</span>
                            : <React.Fragment> <a
                                className={`cwt-name ${userCardVisible ? "cwt-link":""}`}
                                onMouseEnter={(e) => this.showUserCard(e, user.id, userCardVisible)}
                                {...openProfilePage(componentConnector, theme, user.id, market, "_blank")}
                                {...renderHybrid(this.telemetryObjects && this.telemetryObjects.userNameLink, false)}
                            >{user.name}</a>
                            { !isSuperApp &&
                                <i className="cwt-link cwt-visible-on-focus" role="button" aria-label="Show user card" onClick={(e) => this.showUserCard(e, user.id, userCardVisible, false)}> {chevron_down} </i>
                            }
                            </React.Fragment>
                        }
                        <span className="cwt-posted-at dot-before">{getAgoText(createDatetime)}</span>
                    </header>
                </section>
                {this.state.userCardVisible &&
                    <UserCard
                        {...{usersConnector, users, managedClasses, me, t, id: this.state.userCardUserId, visible: true, market,
                            onHide: () => this.setState({userCardVisible: false}),
                            triggerRef: { current: this.ref } as React.RefObject<any>,
                            isHover: this.state.isHoverUserCard,
                            mainTelemetryObject,
                            responsiveClass
                        }}
                    />
                }

                <div className="cwt-message cwt-align-to-header">
                    <div className={messageTruncated == null || messageTruncated ? "cwt-clamp" : ""} ref={this.messageRef}>{commentBody}</div>
                    { messageTruncated && !isBlury && <label className="cwt-show-clamped-btn cwt-link" role="button" onClick={() => this.setState({messageTruncated: false})}>{t.CommentItemSeeMore}</label>}
                </div>

                {!isHidden && !isBlury &&
                <section>
                    <footer className="cwt-footer no-select cwt-align-to-header">
                        {isHidden && (
                            <i className="cwt-icon cwt-thread-locked-message">
                                {shield}
                                <span className="cwt-icon-label">{t.CommentReactionDisabledMsg}</span>
                            </i>
                        )}

                        {shouldFooterRenderTwoLines ? (
                            <React.Fragment>
                                {this.renderReactionsCount(false)}
                                <div className="cwt-line-break"></div>
                                {this.renderReactionPanel({ reactionAllowed, contentDisabled, t, rating, managedClasses, responsiveClass })}
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                {this.renderReactionPanel({ reactionAllowed, contentDisabled, t, rating, managedClasses, responsiveClass })}
                                {this.renderReactionsCount(true)}
                            </React.Fragment>
                        )}

                        {!isSelf ? (
                            <React.Fragment>
                                <i
                                    id={`reportMenu_${id}`}
                                    className="cwt-report-btn cwt-link cwt-icon"
                                    role="listbox"
                                    aria-haspopup="true"
                                    aria-expanded={this.state.reportMenuVisible}
                                    aria-label={t.AriaLabelsOpenReportCommentMenu}
                                    onClick={(e) => this.showReportMenu(e)}
                                    {...renderHybrid(this.telemetryObjects && this.telemetryObjects.reportButton, false)}
                                >
                                    {flag}
                                    <span className="cwt-icon-label">{t.CommentReportText}</span>
                                    {reportMenuVisible && (
                                        <ReportMenu
                                            onReport={this.reportHandler.bind(this)}
                                            onHide={() => this.setState({ reportMenuVisible: false })}
                                            {...{
                                                t,
                                                managedClasses,
                                                id,
                                                me,
                                                visible: reportMenuVisible,
                                                mainTelemetryObject: this.telemetryObjects && this.telemetryObjects.commentItem,
                                                theme: theme,
                                                popupConnector: this.props.popupConnector,
                                                usersConnector: this.props.usersConnector,
                                                responsiveClass: this.props.responsiveClass,
                                                renderInPopover: true
                                            }}
                                        />
                                    )}
                                </i>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <i
                                    id={`deleteMenu_${id}`}
                                    className="cwt-report-btn cwt-link cwt-icon"
                                    role="listbox"
                                    aria-haspopup="true"
                                    aria-expanded={this.state.deleteMenuVisible}
                                    aria-label={t.AriaLabelsOpenDeleteCommentMenu}
                                    onClick={(e) => this.showDeleteMenu(e)}
                                    {...renderHybrid(this.telemetryObjects && this.telemetryObjects.deleteButton, false)}
                                >
                                    {trash}
                                    <span className="cwt-icon-label">{t.CommentDeleteText}</span>
                                </i>
                                {deleteMenuVisible && (
                                    <DeleteMenu
                                        onConfirmDelete={(e) => this.deleteCommentHandler(e, id, parentId, user.id)}
                                        onHide={() => this.setState({ deleteMenuVisible: false })}
                                        {...{
                                            id,
                                            t,
                                            managedClasses,
                                            className: managedClasses.commentItemDeleteMenuPopover,
                                            visible: deleteMenuVisible,
                                            mainTelemetryObject: this.telemetryObjects && this.telemetryObjects.commentItem
                                        }}
                                    />
                                )}
                            </React.Fragment>

                        )}
                    </footer>
                </section>
                }

            </React.Fragment>;
        }

        const replyList = <ReplyList {...{componentConnector, responsiveClass, id, user, replyCount, repliesHasMore, repliesHasPrevious, contentDisabled, me, t, managedClasses, showReplyBox, hideReplyBox, commentConnector, replies, commentItemType, parentId, usersConnector, popupConnector, users, market, content, mainTelemetryObject, theme, specificCommentId }}/>;
        const userAvatar = <img
            alt={t.AriaLabelsProfilePicture}
            className="cwt-avatar cwt-avatar-md"
            src={!showAvatar ? placeholderImages.avatar : avatarUrl}
            onError={(e)=>{ avatarFallback(e); }}
            width="40" height="40"
        />;
        return (
            <li className={classNames(
                managedClasses.commentItemContainer,
                [managedClasses.commentItemOverlayContainer, this.props.commentDisplayType === "overlay"],
                "cwt-comment-highlight",
                ["cwt-specific-comment", isSpecificComment],
                ["cwt-specific-reply", isSpecificComment && isReply],
                responsiveClass,
                [managedClasses.commentItemFooterTwoLines, shouldFooterRenderTwoLines]
            )} aria-hidden={isBlury} {...renderHybrid(this.telemetryObjects && this.telemetryObjects.commentItem, true, (el) => { this.ref = el; })}>
                <div className="cwt-comment-content">
                    {showAvatar ? <a
                        className={`cwt-avatar-link ${userCardVisible? "cwt-link":""}`}
                        tabIndex={isBlury? -1 : 0}
                        onMouseEnter={(e) => this.showUserCard(e, user.id, userCardVisible)}
                        onFocus={(e) => this.showUserCard(e, user.id, userCardVisible)}
                        onBlur={() => this.setState({userCardVisible: false})}
                        {...openProfilePage(componentConnector, theme, user.id, market, "_blank")}
                        {...renderHybrid(this.telemetryObjects && this.telemetryObjects.userAvatarLink, false)}
                    >
                        {userAvatar}
                    </a> : userAvatar}

                    <div className="cwt-inner-content">
                        {innerContent}
                        {!isReply && !isBlury ? replyList : ""}
                    </div>
                    {isReply && !isBlury ? replyList : ""}
                </div>
            </li>
        );
    }

    checkContainerWidth() {
        const dist = this.ref && this.ref && this.ref.offsetWidth - minFooterSingleLineWidth;
        if (dist > 5 && this.state.shouldFooterRenderTwoLines) {
            this.setState({
                shouldFooterRenderTwoLines: false
            });
        } else if (dist < -5 && !this.state.shouldFooterRenderTwoLines) {
            this.setState({
                shouldFooterRenderTwoLines: true
            });
        }
    }

    renderReactionPanel(renderReactionPanelParams) {
        const { reactionAllowed, contentDisabled, t, rating, managedClasses, responsiveClass } = renderReactionPanelParams;
        return reactionAllowed && !contentDisabled && (
            <ReactionPanel
                onReaction={this.reactionHandler.bind(this)}
                onReplyClick={this.replyButtonClickHandler.bind(this)}
                showCommentsButton={true}
                {...{
                    t,
                    rating,
                    managedClasses,
                    mainTelemetryObject: this.telemetryObjects && this.telemetryObjects.commentItem,
                    responsiveClass
                }}
            />
        );
    }

    renderReactionsCount(showSeperator) {
        const { item: { reactionCounts }, t, managedClasses, theme, responsiveClass } = this.props;

        return <SocialMostReactions
            containerClassName="cwt-reaction-counts"
            onReactionsClick={(e) => this.onReactionsClick(e)}
            hasSeperatorBefore={showSeperator}
            telemetryObject={this.telemetryObjects && this.telemetryObjects.reactionsCount}
            {...{ reactionCounts, t, managedClasses, theme, responsiveClass}}
        />;
    }
}
export default CommentItem;
